import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X,
  Loader2,
  Users,
  Calendar,
  CreditCard,
  Flag,
  LifeBuoy,
  ArrowUpRight,
  Clock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatMoney = (n) => `${Number(n || 0).toFixed(2)}€`;
const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const TABS = [
  { id: 'transactions', label: 'Transactions', icon: Calendar, color: '#5BC0EB', tab: 'finance' },
  { id: 'guests',       label: 'Guests',       icon: Users,    color: '#00D976', tab: 'guests' },
  { id: 'reports',      label: 'Reports',      icon: Flag,     color: '#FF6B7A', tab: 'reports' },
  { id: 'support',      label: 'Support',      icon: LifeBuoy, color: '#C792EA', tab: 'support' },
];

const EmptyMessage = ({ children }) => (
  <p style={{ margin: 0, color: 'rgba(255,255,255,0.5)', fontSize: 13, padding: '12px 4px' }}>{children}</p>
);

const Row = ({ children, onClick, testid }) => (
  <div
    data-testid={testid}
    onClick={onClick}
    style={{
      padding: '10px 12px',
      borderRadius: 10,
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'background 140ms ease, border-color 140ms ease',
    }}
    onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; } }}
    onMouseLeave={(e) => { if (onClick) { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; } }}
  >
    {children}
  </div>
);

export const HeartbeatHourDetailModal = ({ open, hourIso, headers, initialTab, onNavigate, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab || 'transactions');

  useEffect(() => {
    if (!open || !hourIso) return undefined;
    let cancelled = false;
    const loadHour = async () => {
      setActiveTab(initialTab || 'transactions');
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/analytics/heartbeat-hour-detail`, {
          headers,
          params: { hour_iso: hourIso },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (cancelled) return;
        toast.error(err.response?.data?.detail || 'Failed to load hour detail');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadHour();
    return () => { cancelled = true; };
  }, [open, hourIso, headers, initialTab]);

  if (!open) return null;

  const totals = data?.totals || { guests: 0, transactions: 0, revenue: 0, reports: 0, support: 0 };
  const rows = data?.[activeTab] || [];
  const activeMeta = TABS.find((t) => t.id === activeTab);

  return (
    <div
      data-testid="heartbeat-hour-detail-modal"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,17,21,0.72)',
        backdropFilter: 'blur(8px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #0F1115 0%, #1A1D24 100%)',
          color: '#FFFFFF',
          width: '100%',
          maxWidth: 640,
          maxHeight: '88vh',
          borderRadius: 22,
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 24px 56px rgba(0,0,0,0.45)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ padding: 18, borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Clock size={16} color="#00D976" />
              {data?.hour_label || '--:--'} hour — {data?.date_label || ''}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
              {totals.guests} guests · {totals.transactions} transactions · {formatMoney(totals.revenue)} Allure net · {totals.reports} reports · {totals.support} support
            </p>
          </div>
          <button
            data-testid="heartbeat-hour-detail-close"
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 9999, color: '#FFF', cursor: 'pointer', width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Tab strip */}
        <div style={{ display: 'flex', gap: 6, padding: '12px 18px 0', flexWrap: 'wrap' }}>
          {TABS.map((t) => {
            const Icon = t.icon;
            const value = t.id === 'transactions' ? totals.transactions : totals[t.id];
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`heartbeat-hour-detail-tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                style={{
                  padding: '7px 12px',
                  borderRadius: 9999,
                  background: active ? t.color : 'rgba(255,255,255,0.06)',
                  color: active ? '#0F1115' : '#FFF',
                  border: `1px solid ${active ? t.color : 'rgba(255,255,255,0.08)'}`,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: 'inherit',
                }}
              >
                <Icon size={12} /> {t.label} <span style={{ opacity: 0.7 }}>· {value}</span>
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loading ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.55)' }}>
              <Loader2 size={14} className="animate-spin" /> Loading hour…
            </div>
          ) : null}

          {!loading && activeTab === 'transactions' ? (
            rows.length === 0 ? <EmptyMessage>No paid transactions in this hour.</EmptyMessage> : rows.map((t) => (
              <Row key={t.id} testid={`heartbeat-tx-${t.id}`}>
                <CreditCard size={14} color="#5BC0EB" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.venue_name}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                    {formatTime(t.created_at)} · {t.guest_name} · paid {formatMoney(t.discounted_amount)}
                  </p>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#FFB454' }}>+{formatMoney(t.allure_net_keep)}</span>
              </Row>
            ))
          ) : null}

          {!loading && activeTab === 'guests' ? (
            rows.length === 0 ? <EmptyMessage>No new guest sign-ups in this hour.</EmptyMessage> : rows.map((g) => (
              <Row key={g.id} testid={`heartbeat-guest-${g.id}`}>
                <Users size={14} color="#00D976" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                    {formatTime(g.created_at)} · {g.email}{g.city ? ` · ${g.city}` : ''}
                  </p>
                </div>
              </Row>
            ))
          ) : null}

          {!loading && activeTab === 'reports' ? (
            rows.length === 0 ? <EmptyMessage>No venue reports filed in this hour.</EmptyMessage> : rows.map((r) => (
              <Row key={r.id} testid={`heartbeat-report-${r.id}`}>
                <Flag size={14} color="#FF6B7A" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{r.venue_name}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                    {formatTime(r.created_at)} · {r.reason || 'No reason given'} · {r.status}
                  </p>
                </div>
              </Row>
            ))
          ) : null}

          {!loading && activeTab === 'support' ? (
            rows.length === 0 ? <EmptyMessage>No support requests in this hour.</EmptyMessage> : rows.map((s) => (
              <Row key={s.id} testid={`heartbeat-support-${s.id}`}>
                <LifeBuoy size={14} color="#C792EA" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.subject}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                    {formatTime(s.created_at)} · {s.user_email} · {s.status}
                  </p>
                </div>
              </Row>
            ))
          ) : null}
        </div>

        {/* Footer: navigate to full tab */}
        {activeMeta && typeof onNavigate === 'function' ? (
          <div style={{ padding: 14, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              data-testid="heartbeat-hour-detail-open-tab"
              onClick={() => { onNavigate(activeMeta.tab); onClose(); }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 9999,
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.14)',
                color: '#FFF',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 12,
                fontFamily: 'inherit',
              }}
            >
              Open {activeMeta.label.toLowerCase()} tab <ArrowUpRight size={12} />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default HeartbeatHourDetailModal;
